import React, { useEffect, useMemo, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import { Document, Page, pdfjs } from 'react-pdf';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const OFFICE_PREVIEW_EXTENSIONS = ['xls', 'xlsx', 'doc', 'docx', 'ppt', 'pptx'];

/** Read-only Office embed (no ribbon). view.aspx shows the full editor chrome. */
export const getOfficePreviewEmbedUrl = (fileUrl) => {
  const params = new URLSearchParams({
    src: fileUrl,
    wdPrint: '0',
    wdEmbedCode: '0',
  });
  return `https://view.officeapps.live.com/op/embed.aspx?${params}`;
};

const safeDecodeURIComponent = (value = '') => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const getFileExtension = (filename = '', fileUrl = '') => {
  const cleanName = filename.split('?')[0].split('#')[0];
  if (cleanName.includes('.')) {
    return cleanName.split('.').pop().toLowerCase();
  }

  const decodedUrl = safeDecodeURIComponent(fileUrl).split('?')[0].split('#')[0];
  if (decodedUrl.includes('.')) {
    return decodedUrl.split('.').pop().toLowerCase();
  }

  return '';
};

const AttachmentPreviewContent = ({ url, filename }) => {
  const extension = useMemo(() => getFileExtension(filename, url), [filename, url]);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfPages, setPdfPages] = useState(0);
  const [pdfZoom, setPdfZoom] = useState(1);
  const [docxLoading, setDocxLoading] = useState(false);
  const [docxError, setDocxError] = useState('');
  const docxContainerRef = useRef(null);

  useEffect(() => {
    setPdfPage(1);
    setPdfPages(0);
    setPdfZoom(1);
  }, [url]);

  useEffect(() => {
    if (extension !== 'docx' || !docxContainerRef.current) return;

    let cancelled = false;
    const currentContainer = docxContainerRef.current;
    currentContainer.innerHTML = '';
    setDocxError('');
    setDocxLoading(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load DOCX file');
        }
        return response.blob();
      })
      .then((blob) =>
        renderAsync(blob, currentContainer, null, {
          className: 'attachment-docx-content',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: true,
          renderHeaders: true,
          renderFooters: true,
        })
      )
      .then(() => {
        if (!cancelled) {
          setDocxLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDocxError('Unable to preview this DOCX file.');
          setDocxLoading(false);
        }
      });

    return () => {
      cancelled = true;
      currentContainer.innerHTML = '';
    };
  }, [extension, url]);

  if (extension === 'pdf') {
    return (
      <div className='attachment-preview-native'>
        <div className='d-flex align-items-center justify-content-between mb-3'>
          <div className='d-flex align-items-center'>
            <button
              type='button'
              className='general-button px-3 py-2 mr-2'
              disabled={pdfPage <= 1}
              onClick={() => setPdfPage((prev) => Math.max(1, prev - 1))}
            >
              Prev page
            </button>
            <span className='paragraph-p3 main-subtle-text'>
              {pdfPages > 0 ? `${pdfPage}/${pdfPages}` : 'Loading...'}
            </span>
            <button
              type='button'
              className='general-button px-3 py-2 ml-2'
              disabled={pdfPages === 0 || pdfPage >= pdfPages}
              onClick={() => setPdfPage((prev) => Math.min(pdfPages, prev + 1))}
            >
              Next page
            </button>
          </div>
          <div className='d-flex align-items-center'>
            <button
              type='button'
              className='general-button px-3 py-2 mr-2'
              disabled={pdfZoom <= 0.7}
              onClick={() => setPdfZoom((prev) => Math.max(0.7, +(prev - 0.1).toFixed(2)))}
            >
              -
            </button>
            <span className='paragraph-p3 main-subtle-text'>{Math.round(pdfZoom * 100)}%</span>
            <button
              type='button'
              className='general-button px-3 py-2 ml-2'
              disabled={pdfZoom >= 2}
              onClick={() => setPdfZoom((prev) => Math.min(2, +(prev + 0.1).toFixed(2)))}
            >
              +
            </button>
          </div>
        </div>
        <div className='attachment-pdf-canvas-wrapper'>
          <Document
            file={url}
            onLoadSuccess={({ numPages }) => {
              setPdfPages(numPages);
              setPdfPage((prev) => Math.min(prev, numPages));
            }}
            loading={<p className='paragraph-general mb-0 p-3'>Loading PDF...</p>}
            error={<p className='paragraph-general mb-0 p-3'>Unable to preview this PDF file.</p>}
          >
            <Page
              pageNumber={pdfPage}
              scale={pdfZoom}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              className='attachment-pdf-page'
            />
          </Document>
        </div>
      </div>
    );
  }

  if (extension === 'docx') {
    return (
      <div className='attachment-preview-native'>
        {docxLoading && <p className='paragraph-general mb-3'>Loading DOCX...</p>}
        {docxError && <p className='paragraph-general mb-3'>{docxError}</p>}
        <div className='attachment-docx-wrapper' ref={docxContainerRef} />
      </div>
    );
  }

  if (OFFICE_PREVIEW_EXTENSIONS.includes(extension)) {
    return (
      <iframe
        src={getOfficePreviewEmbedUrl(url)}
        title={`preview-${filename}`}
        className='attachment-preview-iframe'
        loading='lazy'
        referrerPolicy='no-referrer'
      />
    );
  }

  return <p className='paragraph-general mb-0'>Preview is not available for this file type.</p>;
};

export default AttachmentPreviewContent;
